/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { APP_VERSION } from "@packages/sdk";
import { compare } from "compare-versions";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";

import type { ThemeType } from "../../../../global/emotion";
import {
  numericPercentages,
  radius,
  spacing,
  stroke,
  zIndices,
} from "../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../_base/_core/Button/Button.emotion";

const buttonStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.accentLowest,
  borderRadius: radius.m,
  borderWidth: stroke.regular,
  borderColor: theme.semanticColors.accentMedLow,
  borderStyle: "solid",
  color: theme.semanticColors.accentHighest,
  height: "auto",
  paddingBlock: spacing.xs,
  position: "fixed",
  right: numericPercentages[50],
  top: spacing.l,
  transform: "translateX(50%)",
  whiteSpace: "normal",
  zIndex: zIndices.toast,
  ":focus-visible, :hover, :active": {
    background: theme.semanticColors.accentVeryLow,
    color: theme.semanticColors.accentVeryHigh,
    borderColor: theme.semanticColors.accentMedium,
  },
  [cssMediaQueryMinWidthTablet]: {
    right: spacing.m,
    top: spacing.m,
    transform: "none",
  },
});

export const AppUpgradeCheck = ({
  app,
  children,
}: {
  app:
    | "onboarding"
    | "payments"
    | "the_word"
    | "tool"
    | "transcript_v2"
    | "trivia"
    | "web_app";
  children?: ReactNode;
}) => {
  const [status, setStatus] = useState<"current" | "checking" | "available">(
    "current",
  );
  const showNotification = useMemo(() => status === "available", [status]);
  const { track } = useAnalytics();

  useEffect(() => {
    const check = async () => {
      setStatus("checking");
      const v = await (
        await fetch("/version.json", { cache: "no-store" })
      ).json();
      if (!v?.version) {
        setStatus("current");
        return;
      }
      if (compare(v.version, APP_VERSION, ">")) {
        setStatus("available");
        clearInterval(interval);
        return;
      }
      setStatus("current");
    };

    check();
    const interval = setInterval(() => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "visible"
      ) {
        check();
      }
    }, 30_000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const reloadPage = () => {
    window.location.reload();
  };

  if (showNotification) {
    return (
      <Button
        size="s"
        onClick={reloadPage}
        onClickCapture={() =>
          track({
            event: HallowAnalyticsEvent.TappedRefreshApp,
            properties: { app },
          })
        }
        css={buttonStyle}
      >
        {children ?? <I18nMessage message="web_app_out_of_date" />}
      </Button>
    );
  }

  return null;
};

export default AppUpgradeCheck;
